import { useEffect } from 'react';

interface JsonLdProps {
  data: object;
}

export function JsonLd({ data }: JsonLdProps) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    script.id = 'json-ld-' + Math.random().toString(36).substr(2, 9);
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById(script.id);
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [data]);

  return null;
}

// Organization schema for IMRACK
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "IMRACK",
  "url": "https://imrack.com",
  "logo": "https://imrack.com/logo.png",
  "description": "Enterprise Data Center Infrastructure Management (DCIM) Software for asset inventory, IPAM, cable management, and change control.",
  "sameAs": [],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "sales",
    "url": "https://imrack.com/contact"
  }
};

// Software Application schema
export const softwareSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "IMRACK DCIM Platform",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web Browser",
  "description": "Complete data center infrastructure management platform featuring asset inventory, IP address management (IPAM), cable management, change control, and enterprise reporting.",
  "offers": {
    "@type": "Offer",
    "availability": "https://schema.org/InStock",
    "priceSpecification": {
      "@type": "PriceSpecification",
      "priceCurrency": "USD"
    }
  },
  "featureList": [
    "Asset Inventory Management",
    "IP Address Management (IPAM)",
    "Cable Management & Tracking",
    "Change Management Workflows",
    "RESTful API & Webhooks",
    "Scheduled Reports & Analytics",
    "Multi-tenant Architecture",
    "Role-Based Access Control",
    "Audit Logging & Compliance"
  ],
  "publisher": {
    "@type": "Organization",
    "name": "IMRACK"
  }
};

// FAQ schema generator
export function generateFaqSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

// Breadcrumb schema generator
export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
}

// Article schema generator for blog posts
export function generateArticleSchema(article: {
  title: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "url": article.url,
    "datePublished": article.datePublished,
    "dateModified": article.dateModified || article.datePublished,
    "author": {
      "@type": "Organization",
      "name": article.author || "IMRACK"
    },
    "publisher": {
      "@type": "Organization",
      "name": "IMRACK",
      "logo": {
        "@type": "ImageObject",
        "url": "https://imrack.com/logo.png"
      }
    },
    ...(article.image && { "image": article.image })
  };
}
